import { useEffect, useState } from "react";
import { NavLink, useLocation } from "react-router-dom";
import { Home, Flame, Cake, Palette, Briefcase, Sparkles, Wrench, BookOpenCheck, Store, Hand, Cookie, Wheat, CroissantIcon, PartyPopper, BookOpen, Scale, Droplets, Leaf, Replace, Calculator, FileText, Package, Tag, TrendingUp, ScrollText, Instagram, Mail, Camera, MapPin, X, TestTube2, Container } from "lucide-react";

const CAT_ICONS = {
  "introductions":     Hand,
  "general-baking":    Cookie,
  "bread-sourdough":   Wheat,
  "cakes":             Cake,
  "cake-pops":         Cake,
  "pastries-desserts": CroissantIcon,
  "recipes-sharing":   BookOpen,
  "troubleshooting":   Wrench,
  "off-topic-fun":     PartyPopper,
  "testers-chat":      TestTube2,
};

const CAT_ICON_COLORS = {
  "introductions":     "#F4A83B",
  "general-baking":    "#8EAC50",
  "bread-sourdough":   "#B5834B",
  "cakes":             "#EA6D9E",
  "cake-pops":         "#FF7E67",
  "pastries-desserts": "#E8842B",
  "recipes-sharing":   "#4C9F70",
  "troubleshooting":   "#C2185B",
  "off-topic-fun":     "#8E7CC3",
  "testers-chat":      "#2F80ED",
};

export default function Sidebar({ categories = [] }) {
  const [openDrawer, setOpenDrawer] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const toggle = () => setOpenDrawer((v) => !v);
    window.addEventListener("cp-toggle-sidebar", toggle);
    return () => window.removeEventListener("cp-toggle-sidebar", toggle);
  }, []);

  // Close drawer on route change
  useEffect(() => { setOpenDrawer(false); }, [location.pathname, location.search]);

  return (
    <>
      {/* Mobile drawer overlay */}
      <div
        className={`lg:hidden fixed inset-0 z-50 transition ${openDrawer ? "pointer-events-auto" : "pointer-events-none"}`}
        aria-hidden={!openDrawer}
      >
        <div
          className={`absolute inset-0 bg-black/50 transition-opacity ${openDrawer ? "opacity-100" : "opacity-0"}`}
          onClick={() => setOpenDrawer(false)}
        />
        <aside
          className={`absolute top-0 left-0 h-full w-[85%] max-w-[320px] bg-white shadow-2xl overflow-y-auto transition-transform ${
            openDrawer ? "translate-x-0" : "-translate-x-full"
          }`}
          data-testid="sidebar-drawer"
        >
          <div className="flex items-center justify-between px-4 py-3 border-b border-[#EBDCD1] sticky top-0 bg-white z-10">
            <div className="font-heading font-bold text-lg text-[#3B2C24]">Menu</div>
            <button
              onClick={() => setOpenDrawer(false)}
              className="p-1.5 hover:bg-[#F9EFE6] rounded-full"
              aria-label="Close menu"
              data-testid="sidebar-drawer-close"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
          <nav className="p-3 space-y-1">
            <SidebarLinks categories={categories} />
          </nav>
        </aside>
      </div>

      {/* Desktop persistent sidebar */}
      <aside className="hidden lg:block" data-testid="sidebar">
        <nav className="cp-card p-3 space-y-1 sticky top-20">
          <SidebarLinks categories={categories} />
        </nav>
      </aside>
    </>
  );
}

function SidebarLinks({ categories }) {
  return (
    <>
        <NavLink
          to="/"
          end
          className={({ isActive }) =>
            `flex items-center gap-3 px-3 py-2.5 rounded-2xl transition ${
              isActive ? "bg-[#FF7E67]/10 text-[#FF7E67] font-semibold" : "hover:bg-[#F9EFE6] text-[#3B2C24]"
            }`
          }
          data-testid="nav-home"
        >
          <Home className="w-4 h-4" /> <span className="text-sm">Home</span>
        </NavLink>
        <NavLink
          to="/?sort=trending"
          className="flex items-center gap-3 px-3 py-2.5 rounded-2xl hover:bg-[#F9EFE6] text-[#3B2C24] transition"
          data-testid="nav-trending"
        >
          <Flame className="w-4 h-4 text-[#FF7E67]" /> <span className="text-sm">Trending</span>
        </NavLink>
        <NavLink
          to="/find-a-baker"
          className={({ isActive }) =>
            `flex items-center gap-3 px-3 py-2.5 rounded-2xl transition ${
              isActive ? "bg-[#FF7E67]/10 text-[#FF7E67] font-semibold" : "hover:bg-[#F9EFE6] text-[#3B2C24]"
            }`
          }
          data-testid="nav-find-a-baker"
        >
          <MapPin className="w-4 h-4" style={{ color: "#2F80ED" }} /> <span className="text-sm">Find a Baker</span>
        </NavLink>
        <NavLink
          to="/store-front"
          className={({ isActive }) =>
            `flex items-center gap-3 px-3 py-2.5 rounded-2xl transition ${
              isActive ? "bg-[#FF7E67]/10 text-[#FF7E67] font-semibold" : "hover:bg-[#F9EFE6] text-[#3B2C24]"
            }`
          }
          data-testid="nav-marketplace"
        >
          <Store className="w-4 h-4 text-[#8EAC50]" />
          <span className="text-sm">Store Fronts</span>
        </NavLink>
        <NavLink
          to="/classifieds"
          className={({ isActive }) =>
            `flex items-center gap-3 px-3 py-2.5 rounded-2xl transition ${
              isActive ? "bg-[#FF7E67]/10 text-[#FF7E67] font-semibold" : "hover:bg-[#F9EFE6] text-[#3B2C24]"
            }`
          }
          data-testid="nav-classifieds"
        >
          <Tag className="w-4 h-4" style={{ color: "#8EAC50" }} /> <span className="text-sm">Classifieds</span>
        </NavLink>

        <div className="px-3 pt-4 pb-1 text-[11px] uppercase tracking-widest text-[#857163] font-medium">Forum Categories</div>
        {categories.map((c) => {
          const Icon = CAT_ICONS[c.slug] || Cake;
          const color = CAT_ICON_COLORS[c.slug] || "#8EAC50";
          const isTesterChat = c.slug === "testers-chat";
          return (
            <NavLink
              key={c.slug}
              to={`/c/${c.slug}`}
              className={({ isActive }) =>
                `flex items-center justify-between gap-2 px-3 py-2.5 rounded-2xl transition border ${
                  isTesterChat
                    ? "border-[#F4A83B]/60 bg-[#FFD07F]/40 hover:bg-[#FFD07F]/60 text-[#3B2C24] font-semibold shadow-[0_0_0_2px_rgba(244,168,59,0.15)]"
                    : `border-transparent ${isActive ? "bg-[#FFD07F]/25 text-[#3B2C24] font-semibold" : "hover:bg-[#F9EFE6] text-[#3B2C24]"}`
                }`
              }
              data-testid={`nav-category-${c.slug}`}
            >
              <span className="flex items-center gap-3">
                <Icon className="w-4 h-4" style={{ color: isTesterChat ? "#B26A00" : color }} />
                <span className="text-sm">{c.name}</span>
              </span>
              <span className={`text-xs ${isTesterChat ? "text-[#B26A00] font-semibold" : "text-[#857163]"}`}>{c.post_count}</span>
            </NavLink>
          );
        })}

        <div className="px-3 pt-4 pb-1 text-[11px] uppercase tracking-widest text-[#857163] font-medium">Baker's Tools</div>
        <NavLink
          to="/analyzer"
          className={({ isActive }) =>
            `flex items-center gap-3 px-3 py-2.5 rounded-2xl transition ${
              isActive ? "bg-[#F4A83B]/15 text-[#3B2C24] font-semibold" : "hover:bg-[#F9EFE6] text-[#3B2C24]"
            }`
          }
          data-testid="nav-analyzer"
        >
          <Sparkles className="w-4 h-4" style={{ color: "#F4A83B" }} /> <span className="text-sm">AI Crumb Analyzer</span>
        </NavLink>
        <NavLink
          to="/scaler"
          className={({ isActive }) =>
            `flex items-center gap-3 px-3 py-2.5 rounded-2xl transition ${
              isActive ? "bg-[#F4A83B]/15 text-[#3B2C24] font-semibold" : "hover:bg-[#F9EFE6] text-[#3B2C24]"
            }`
          }
          data-testid="nav-scaler"
        >
          <Scale className="w-4 h-4" style={{ color: "#8EAC50" }} /> <span className="text-sm">Recipe Scaler</span>
        </NavLink>
        <NavLink
          to="/calculator"
          className={({ isActive }) =>
            `flex items-center gap-3 px-3 py-2.5 rounded-2xl transition ${
              isActive ? "bg-[#F4A83B]/15 text-[#3B2C24] font-semibold" : "hover:bg-[#F9EFE6] text-[#3B2C24]"
            }`
          }
          data-testid="nav-calculator"
        >
          <Droplets className="w-4 h-4" style={{ color: "#2F80ED" }} /> <span className="text-sm">Hydration Calculator</span>
        </NavLink>
        <NavLink
          to="/adaptor"
          className={({ isActive }) =>
            `flex items-center gap-3 px-3 py-2.5 rounded-2xl transition ${
              isActive ? "bg-[#F4A83B]/15 text-[#3B2C24] font-semibold" : "hover:bg-[#F9EFE6] text-[#3B2C24]"
            }`
          }
          data-testid="nav-adaptor"
        >
          <Leaf className="w-4 h-4" style={{ color: "#4C9F70" }} /> <span className="text-sm">Dietary Adaptor</span>
        </NavLink>
        <NavLink
          to="/substitute"
          className={({ isActive }) =>
            `flex items-center gap-3 px-3 py-2.5 rounded-2xl transition ${
              isActive ? "bg-[#F4A83B]/15 text-[#3B2C24] font-semibold" : "hover:bg-[#F9EFE6] text-[#3B2C24]"
            }`
          }
          data-testid="nav-substitute"
        >
          <Replace className="w-4 h-4" style={{ color: "#E8842B" }} /> <span className="text-sm">Substitution Guide</span>
        </NavLink>
        <NavLink
          to="/my-recipes"
          className={({ isActive }) =>
            `flex items-center gap-3 px-3 py-2.5 rounded-2xl transition ${
              isActive ? "bg-[#F4A83B]/15 text-[#3B2C24] font-semibold" : "hover:bg-[#F9EFE6] text-[#3B2C24]"
            }`
          }
          data-testid="nav-my-recipes"
        >
          <BookOpen className="w-4 h-4" style={{ color: "#4C9F70" }} /> <span className="text-sm">My Recipes</span>
        </NavLink>

        <div className="px-3 pt-4 pb-1 text-[11px] uppercase tracking-widest text-[#857163] font-medium">Business & Selling</div>
        {[
          { to: "/costing",      testid: "nav-costing",      icon: Calculator, color: "#F4A83B", label: "Costing & Profit" },
          { to: "/invoice",      testid: "nav-invoice",      icon: FileText,   color: "#2F80ED", label: "Invoice & Quotes" },
          { to: "/inventory",    testid: "nav-inventory",    icon: Package,    color: "#8EAC50", label: "Inventory & Stock" },
          { to: "/label",        testid: "nav-label",        icon: Tag,        color: "#C2185B", label: "Label Designer" },
          { to: "/ledger",       testid: "nav-ledger",       icon: TrendingUp, color: "#4C9F70", label: "Sales & Expenses" },
        ].map((it) => (
          <NavLink
            key={it.to}
            to={it.to}
            className={({ isActive }) =>
              `flex items-center gap-3 px-3 py-2.5 rounded-2xl transition ${
                isActive ? "bg-[#F4A83B]/15 text-[#3B2C24] font-semibold" : "hover:bg-[#F9EFE6] text-[#3B2C24]"
              }`
            }
            data-testid={it.testid}
          >
            <it.icon className="w-4 h-4" style={{ color: it.color }} /> <span className="text-sm">{it.label}</span>
          </NavLink>
        ))}

        <div className="px-3 pt-4 pb-1 text-[11px] uppercase tracking-widest text-[#857163] font-medium">Marketing & Sales</div>
        {[
          { to: "/logo-maker",     testid: "nav-logo-maker",     icon: Palette,   color: "#8E7CC3", label: "Logo & Branding" },
          { to: "/social-post",    testid: "nav-social-post",    icon: Instagram, color: "#E1306C", label: "Social Media Posts" },
          { to: "/newsletter",     testid: "nav-newsletter",     icon: Mail,      color: "#2F80ED", label: "Email Newsletters" },
          { to: "/photo-enhancer", testid: "nav-photo-enhancer", icon: Camera,    color: "#F4A83B", label: "Photo Enhancer" },
          { to: "/cart-branding",  testid: "nav-cart-branding",  icon: Container, color: "#8EAC50", label: "Cart Branding Preview" },
        ].map((it) => (
          <NavLink
            key={it.to}
            to={it.to}
            className={({ isActive }) =>
              `flex items-center gap-3 px-3 py-2.5 rounded-2xl transition ${
                isActive ? "bg-[#F4A83B]/15 text-[#3B2C24] font-semibold" : "hover:bg-[#F9EFE6] text-[#3B2C24]"
              }`
            }
            data-testid={it.testid}
          >
            <it.icon className="w-4 h-4" style={{ color: it.color }} /> <span className="text-sm">{it.label}</span>
          </NavLink>
        ))}
        <div className="px-3 pt-4 pb-1 text-[11px] uppercase tracking-widest text-[#857163] font-medium">Community</div>
        <NavLink
          to="/rules"
          className={({ isActive }) =>
            `flex items-center gap-3 px-3 py-2.5 rounded-2xl transition ${
              isActive ? "bg-[#FF7E67]/10 text-[#FF7E67] font-semibold" : "hover:bg-[#F9EFE6] text-[#3B2C24]"
            }`
          }
          data-testid="nav-rules"
        >
          <BookOpenCheck className="w-4 h-4 text-[#FF7E67]" /> <span className="text-sm">House Rules</span>
        </NavLink>
    </>
  );
}
